import type { ReactNode } from 'react';
import { Truck } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import {
  useMantenimientoDashboard,
  useStockDashboard,
  useVentasDashboard,
} from './ventasApi';
import { fmtARS, fmtARSCompact, fmtDias, fmtInt, fmtVar, varSign } from './dashFormat';
import { EvolucionVentasChart, RankBars, SERIE_AZUL, type RankRow } from './ventasCharts';
import { KpiCard, KpiSkeletonRow, PendientesList } from './dashUi';

/**
 * Overview — resumen ejecutivo que combina Ventas, Stock y Mantenimiento.
 * Ventas fijas a 12 meses; stock en ARS (dólar oficial no aplica acá).
 */
export function OverviewSub({ nav }: { nav?: ReactNode }) {
  const ventas = useVentasDashboard('12m');
  const stock = useStockDashboard('ars', 'oficial');
  const mnt = useMantenimientoDashboard();

  const navRow = <div className="flex flex-wrap items-center gap-2">{nav}</div>;

  if (ventas.isLoading || !ventas.data) {
    return (
      <div className="flex flex-col gap-4">
        {navRow}
        <KpiSkeletonRow n={5} />
      </div>
    );
  }
  const v = ventas.data;
  const mc = v.mesEnCurso;

  const vendedoresRows: RankRow[] = v.topVendedores.slice(0, 5).map((x) => ({
    label: x.vendedor,
    value: x.total,
    color: SERIE_AZUL,
    valueText: fmtARSCompact(x.total),
    sub: `${fmtInt(x.unidades)} u`,
  }));

  return (
    <div className="flex flex-col gap-4">
      {navRow}
      {/* KPIs combinados */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
        <KpiCard
          label={`Ventas · ${mc.label} (al día ${mc.diaCorte})`}
          value={fmtARS(mc.total)}
          delta={fmtVar(mc.total, mc.totalPrev)}
          sign={varSign(mc.total, mc.totalPrev)}
          foot={`${fmtInt(mc.operaciones)} ventas · ${fmtInt(mc.clientes)} clientes`}
        />
        <KpiCard
          label="Acumulado · 12 meses"
          value={fmtARS(v.periodo.total)}
          foot={`${fmtARSCompact(v.periodo.promedioMensual)}/mes · ${fmtInt(v.periodo.operaciones)} ventas`}
        />
        <KpiCard
          label="Ciclo de venta"
          value={fmtDias(v.ciclo.cotizacionAPago)}
          foot={
            v.ciclo.cotizacionACierre != null
              ? `Cotización→pago · hasta cierre ${fmtDias(v.ciclo.cotizacionACierre)} (aprox.)`
              : `Cotización → pago total · n=${fmtInt(v.ciclo.muestrasPago)}`
          }
        />
        <KpiCard
          label="Stock valorizado"
          value={stock.data ? fmtARS(stock.data.stockTotal.valor) : '…'}
          foot={stock.data ? `${fmtInt(stock.data.stockTotal.unidades)} u a precio de venta` : 'Cargando…'}
        />
        <KpiCard
          label="Mantenimiento pendiente"
          value={mnt.data ? fmtInt(mnt.data.noCerrados) : '…'}
          foot={
            mnt.data
              ? `${fmtInt(mnt.data.faltaDespachar.count)} a despachar · ${fmtInt(mnt.data.pendientePuestaMarcha.count)} a cerrar`
              : 'Cargando…'
          }
          accent={mnt.data && mnt.data.faltaDespachar.count > 0 ? '#d03b3b' : undefined}
        />
      </div>

      {/* Evolución */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Evolución de ventas · 12 meses (por mes de pago)</CardTitle>
        </CardHeader>
        <CardContent>
          <EvolucionVentasChart series={v.series} medida="monto" />
        </CardContent>
      </Card>

      {/* Vendedores + Mantenimiento */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Vendedores destacados · 12m</CardTitle>
          </CardHeader>
          <CardContent>
            <RankBars rows={vendedoresRows} total={v.periodo.total} emptyText="Sin ventas en el período." />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Truck className="h-4 w-4 text-muted-foreground" aria-hidden />
              Hornos a despachar
            </CardTitle>
            {mnt.data && mnt.data.faltaDespachar.count > 0 && (
              <span
                className="rounded-full px-2 py-0.5 text-xs font-semibold text-white"
                style={{ backgroundColor: '#d03b3b' }}
              >
                {fmtInt(mnt.data.faltaDespachar.count)}
              </span>
            )}
          </CardHeader>
          <CardContent>
            {!mnt.data ? (
              <div className="flex h-28 items-center justify-center text-sm text-muted-foreground">
                Cargando…
              </div>
            ) : (
              <PendientesList
                items={mnt.data.faltaDespachar.ventas.slice(0, 6)}
                emptyText="Nada pendiente de despacho."
              />
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
